// SettingsOverlay — the Settings surface as a large centered modal (left sub-nav + content), opened
// from both the sidebar "Settings" item and the account menu. Tabs: General, Data & Retention, Usage,
// Registry (only when the ros-registry flag is on), Agents (only when the reachy-agent-builder flag is
// on). Registry is an admin/config surface, so it lives here rather than as a daily-nav rail item.
// Studio Cyan/Stone tokens; functionality inspired by Claude.ai, look-and-feel is ours.

import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import { AgentsScreen } from './AgentsScreen'
import { SettingsGeneral } from './SettingsGeneral'
import { SettingsUsage } from './SettingsUsage'
import { SettingsDataRetention } from './SettingsDataRetention'
import { RegistryPage } from '../pages/RegistryPage'
import type { UserSettings, UserSettingsPatch } from '../pages/userSettingsApi'

type Tab = 'general' | 'data' | 'usage' | 'registry' | 'agents'

interface NavItem { id: Tab; label: string; icon: React.ReactNode }

export function SettingsOverlay({
  open, onClose, settings, onSave, onUploadAvatar, onFindLocation, onResolveManualLocation, onRemoveLocation, showAgents, showRegistry, showLocation, apiBaseUrl, token,
}: {
  open: boolean
  onClose: () => void
  settings: UserSettings | null
  onSave: (patch: UserSettingsPatch) => Promise<UserSettings>
  onUploadAvatar: (file: File) => Promise<void>
  onFindLocation: () => void
  onResolveManualLocation: (value: string) => Promise<void>
  onRemoveLocation: () => Promise<void>
  showAgents: boolean
  showRegistry: boolean
  showLocation: boolean
  apiBaseUrl: string
  token: string | null
}) {
  const [tab, setTab] = useState<Tab>('general')
  useEffect(() => {
    if ((tab === 'registry' && !showRegistry) || (tab === 'agents' && !showAgents)) setTab('general')
  }, [tab, showRegistry, showAgents])

  const nav: NavItem[] = [
    { id: 'general', label: 'General', icon: <SettingsOutlinedIcon fontSize="small" /> },
    { id: 'data', label: 'Data & Retention', icon: <ShieldOutlinedIcon fontSize="small" /> },
    { id: 'usage', label: 'Usage', icon: <BarChartOutlinedIcon fontSize="small" /> },
    ...(showRegistry ? [{ id: 'registry' as Tab, label: 'Registry', icon: <AppsOutlinedIcon fontSize="small" /> }] : []),
    ...(showAgents ? [{ id: 'agents' as Tab, label: 'Agents', icon: <SmartToyOutlinedIcon fontSize="small" /> }] : []),
  ]

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '80vh', maxHeight: 760, overflow: 'hidden' } }}
    >
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        {/* Left sub-nav */}
        <Box sx={{ width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'rgba(87,82,74,0.03)', p: 2 }}>
          <Typography sx={{ px: 1, mb: 1.5, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>Settings</Typography>
          {nav.map(n => {
            const active = tab === n.id
            return (
              <Box
                key={n.id}
                onClick={() => setTab(n.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: 1.5, cursor: 'pointer', mb: 0.25,
                  color: active ? 'primary.main' : 'text.secondary',
                  bgcolor: active ? 'rgba(0,143,168,0.08)' : 'transparent',
                  fontWeight: active ? 600 : 500, fontSize: '0.88rem',
                  '&:hover': { bgcolor: active ? 'rgba(0,143,168,0.12)' : 'action.hover' },
                }}
              >
                {n.icon}{n.label}
              </Box>
            )
          })}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, color: 'text.secondary' }}><CloseIcon /></IconButton>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {tab === 'general' && (
              <SettingsGeneral
                settings={settings}
                onSave={onSave}
                onUploadAvatar={onUploadAvatar}
                onFindLocation={onFindLocation}
                onResolveManualLocation={onResolveManualLocation}
                onRemoveLocation={onRemoveLocation}
                showLocation={showLocation}
              />
            )}
            {tab === 'data' && <SettingsDataRetention settings={settings} onSave={onSave} />}
            {tab === 'usage' && <SettingsUsage />}
            {/* RegistryPage is the routed admin surface rendered as-is inside the modal. It relies on
                useRosShell() (available app-wide) for its API/token, so it works unchanged here. */}
            {tab === 'registry' && showRegistry && <RegistryPage />}
            {tab === 'agents' && showAgents && (
              <AgentsScreen apiBaseUrl={apiBaseUrl} token={token} onClose={onClose} />
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
